import { PlanogramRenderer } from "./PlanogramRenderer.js";
import { PlanogramCamera } from "./PlanogramCamera.js";
import { PlanogramContainerAttributes, PlanogramRendererResources } from './Resources.js';
import * as THREE from 'three';
import { PlanogramObject } from "./PlanogramObject.js";
import { PlanogramPosition } from "./PlanogramPosition.js";
import { DragArray, SnapDecoration, AnchorData } from "./DragArray.js";
import { PlanogramBlockingPosition } from "./PlanogramBlockingPosition.js";
import { PlanogramBlocking } from "./PlanogramBlocking.js";
import { PlanogramBlockingDivider } from "./PlanogramBlockingDivider.js";
import { PlanogramComponent } from "./PlanogramComponent.js";
import { PlanogramCameraFrame } from "./PlanogramCameraFrame.js";
import { PlanogramSubcomponent } from "./PlanogramSubcomponent.js";

export class PlanogramContainer extends HTMLElement {

    //#region Fields and Attributes

    //#region Static Attributes
    static Attributes = PlanogramContainerAttributes.Attributes;
    //#endregion

    //#region Private Fields

    //#region Selection and Dragging Fields
    /** @type { PlanogramObject|PlanogramBlockingDivider|null } */
    #targetOnDown;
    /**@type {THREE.Vector2|null} */
    #downPosition = null;
    #plane = new THREE.Plane();
    #offset = new THREE.Vector3();
    #intersection = new THREE.Vector3();
    #worldPosition = new THREE.Vector3();
    #inverseMatrix = new THREE.Matrix4();
    #raycaster = new THREE.Raycaster();
    #dragVector = new THREE.Vector3();
    #dividerDragOffset = null;
    #dividerOutline = null;
    #dividerOutlineTarget = null;
    #intersectTarget = new THREE.Vector3();


    // Pointer event tracking for divider dragging
    #pointerDragging = false;
    #activePointerId = null;
    #pointerDragTarget = null;
    //#endregion

    //#region Tab Item Fields
    /**@type {HTMLDivElement} */
    #tabItem = null;
    get tabItem() { return this.#tabItem; }
    /**@type {HTMLButtonElement} */
    #closebutton = null;
    /**@type {HTMLSpanElement} */
    #text = null;
    get cameraFrame() {
        if (!this.groupFrameId) return null;
        const frames = [...this.renderer.querySelectorAll('planogram-camera-frame')];
        return frames.find(f => f.uid === this.groupFrameId);
    }
    //#endregion

    #firstRender = true;
    /**@type {THREE.Scene} */
    #scene = new THREE.Scene();
    /**@type {THREE.Group} */
    #group = new THREE.Group();
    get #targetDraggable() {
        // Allow blocking dividers to be draggable even when not selected
        if (!this.#targetOnDown) return null;
        if (this.#targetOnDown instanceof PlanogramBlockingDivider) return this.#targetOnDown;
        return this.#targetOnDown.selected ? this.#targetOnDown : null;
    }
    get isDraggable() { return this.#targetDraggable !== null; }
    #uid = `planogram-tab-${Math.random().toString(36).substring(2, 9)}`;
    get uid() { return this.#uid; }

    //#endregion

    //#region Public Fields
    get group() { return this.#group; }
    get scene() { return this.#scene; }
    get boundingBox() {
        const components = this.scene.getObjectsByProperty("name", PlanogramComponent.name).map(x => x.userData.boundingBox);
        const positions = this.scene.getObjectsByProperty("name", PlanogramPosition.name).map(x => x.userData.boundingBox);
        const combined = [...components, ...positions].reduce((acc, x) => acc.union(x.clone()), new THREE.Box3());
        return combined;
    }
    get planogramBox() {
        var vector = new THREE.Vector3(this.width, this.height, this.depth);
        const boundingBox = this.boundingBox;
        // if(vector.equals(new THREE.Vector3(0,0,0)))
        vector = boundingBox.getSize(new THREE.Vector3());
        vector.setZ(vector.z + 2);
        return new THREE.Box3().setFromCenterAndSize(boundingBox.getCenter(new THREE.Vector3()), vector);
    }
    /**@type {PlanogramCamera[]} */
    get activeCameras() {
        if (!this.containerId) return [];
        const renderer = PlanogramRenderer.get(this);
        if (!renderer) return [];
        return [...renderer.querySelectorAll(`planogram-camera[container-id='${this.containerId}']`)].filter(x => x.active);
    }
    /**@type {PlanogramCamera|null} */
    get focusedCamera() {
        if (!this.containerId) return null;
        const renderer = PlanogramRenderer.get(this);
        if (!renderer) return null;
        return renderer.querySelector(`planogram-camera[container-id='${this.containerId}'][focused]`) || null;
    }
    /**@type {PlanogramToolBar|null} */
    get toolBar() { return this.querySelector("planogram-toolbar"); }
    //#endregion


    //#region Attribute Properties
    get containerId() { return this.getAttribute(PlanogramContainer.Attributes.ContainerId) || null; }
    get planogramName() { return this.getAttribute(PlanogramContainer.Attributes.PlanogramName) || "Planogram"; }
    get imagesEnabled() { return this.hasAttribute(PlanogramContainer.Attributes.ImagesEnabled); }
    get hideChestWalls() { return this.hasAttribute(PlanogramContainer.Attributes.HideChestWalls); }
    get autoZoom() { return this.hasAttribute(PlanogramContainer.Attributes.AutoZoom); }
    get width() { return Number(this.getAttribute(PlanogramContainer.Attributes.Width)) || 0; }
    get height() { return Number(this.getAttribute(PlanogramContainer.Attributes.Height)) || 0; }
    get depth() { return Number(this.getAttribute(PlanogramContainer.Attributes.Depth)) || 0; }
    get showPositionsThroughBlocking() { return this.hasAttribute(PlanogramContainer.Attributes.ShowPositionsThroughBlocking); }
    get groupName() { return this.getAttribute(PlanogramContainer.Attributes.GroupName) || null; }
    get groupFrameId() { return this.getAttribute(PlanogramContainer.Attributes.GroupFrameId) || null; }
    get groupFrameIndex() { return Number(this.getAttribute(PlanogramContainer.Attributes.GroupFrameIndex)) || -1; }
    get groupFocused() { return this.hasAttribute(PlanogramContainer.Attributes.GroupFocused); }
    //#endregion



    /**@type {DragArray} */
    dragArrayModel = null;

    /**@type {SnapDecoration} */
    snapDecoration = null;

    static get observedAttributes() {
        return [...Object.values(PlanogramContainer.Attributes)];
    }
    //#endregion

    //#endregion
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
    }

    //#region Scene Methods
    #setUpScene() {
        this.#scene = new THREE.Scene();
        this.#scene.background = new THREE.Color(0xffffff);
        this.#group = new THREE.Group();
        this.#group.name = this.planogramName;
        this.#scene.add(this.#group);
        // this.querySelectorAll("planogram-camera").forEach(x=>this.attachGrid(x)); 

    }

    addToScene(item) {
        this.#group.add(item);
    }

    /**
     * 
     * @param {string} uid 
     * @returns {PlanogramPosition|undefined}
     */
    getPlanogramPositionFromUid = (uid) => [...this.querySelectorAll(`planogram-position`)].find(x => x.uid === uid);
    /**
     * 
     * @param {string} uid 
     * @returns {PlanogramComponent|undefined}
     */
    getPlanogramSubComponentFromUid = (uid) => [...this.querySelectorAll(`planogram-component`)].find(x => x.uid === uid);

    /**
     * 
     * @param {string} uid 
     * @returns {PlanogramObject|undefined}
     */
    getPlanogramObjectFromUid = (uid) => {
        var names = [];
        var renderer = PlanogramRenderer.get(this);
        if (!renderer.ignorePositions) names.push("planogram-position");
        if (!renderer.ignoreSubcomponents) names.push("planogram-component");
        if (renderer.includeBlocking) names.push("planogram-blocking");
        return [...this.querySelectorAll(names.join(","))].find(x => x.uid === uid);
    };
    //#endregion

    //#region Spacial Calculation Methods

    /**
     * 
     * @param {PlanogramObject} target 
     * @param {number} clampDetection 
     * @param {THREE.Vector3} position
     * @returns {THREE.Vector3}
     */
    #getNearestObject(position = null) {
        const target = this.dragArrayModel?.target;
        const ignoreIds = this.dragArrayModel?.planogramObjects.map(x => x.uid) ?? [];
        const subComponents = this.#group.getObjectsByProperty("name", PlanogramComponent.name);
        const positions = this.#group.getObjectsByProperty("name", PlanogramPosition.name);
        const targetBox = new THREE.Box3().setFromCenterAndSize(
            (position ? position : new THREE.Vector3(target.x, target.y, target.z)),
            target.groupBoundingBox.getSize(new THREE.Vector3()));
        const closestFaces = [...subComponents, ...positions].filter(x => x instanceof THREE.Group && !ignoreIds.includes(x.userData.id));
        return this.fromNearest(targetBox, closestFaces);
    }

    snapToUnit(position, unit) {
        return new THREE.Vector3(
            Math.round(position.x / unit) * unit,
            Math.round(position.y / unit) * unit,
            Math.round(position.z / unit) * unit
        );
    }

    static getNormalFromFace(face) {
        if (face === PlanogramObject.face.Left) return new THREE.Vector3(-1, 0, 0);
        if (face === PlanogramObject.face.Right) return new THREE.Vector3(1, 0, 0);
        if (face === PlanogramObject.face.Top) return new THREE.Vector3(0, 1, 0);
        if (face === PlanogramObject.face.Bottom) return new THREE.Vector3(0, -1, 0);
        if (face === PlanogramObject.face.Front) return new THREE.Vector3(0, 0, 1);
        if (face === PlanogramObject.face.Back) return new THREE.Vector3(0, 0, -1);
    }

    static getFaceFromNormal(normal) {
        if (normal.equals(new THREE.Vector3(-1, 0, 0))) return PlanogramObject.face.Left;
        if (normal.equals(new THREE.Vector3(1, 0, 0))) return PlanogramObject.face.Right;
        if (normal.equals(new THREE.Vector3(0, 1, 0))) return PlanogramObject.face.Top;
        if (normal.equals(new THREE.Vector3(0, -1, 0))) return PlanogramObject.face.Bottom;
        if (normal.equals(new THREE.Vector3(0, 0, 1))) return PlanogramObject.face.Front;
        if (normal.equals(new THREE.Vector3(0, 0, -1))) return PlanogramObject.face.Back;
    }

    /**
     * 
     * @param {THREE.Box3} target 
     * @param {THREE.Object3D[]} objects 
     * @returns {THREE.Vector3}
     */
    fromNearest(target, objects) {
        const renderer = PlanogramRenderer.get(this);
        const constrainToFace = renderer?.ctrlKey ?? false;
        var constrainVector = constrainToFace;
        const clampDetection = renderer.snappingDistance;
        const gap = renderer.hangingGap;
        const boxPosition = target.getCenter(new THREE.Vector3());
        const boxSize = target.getSize(new THREE.Vector3());
        const objectsWithFaces = objects.flatMap(x => Object.values(PlanogramObject.face).map(face => { return { face, object: x }; }));

        const { volume, newSize, faceBox, objectBox, n, c, normal, object } = objectsWithFaces.reduce((i, o) => {
            const { face, object } = o;
            const normal = PlanogramContainer.getNormalFromFace(face);
            const size = new THREE.Box3().setFromObject(object).getSize(new THREE.Vector3());
            const c = normal.toArray().reduce((a, c, i) => { return (c !== 0 && a === -1) ? i : a; }, -1);
            const n = normal.getComponent(c);
            const faceBox = new THREE.Box3().setFromCenterAndSize(
                object.position.clone().add(new THREE.Vector3().setComponent(c, ((size.getComponent(c) + clampDetection) / 2) * n)),
                size.setComponent(c, clampDetection));
            const intersectedBox = faceBox.clone().intersect(target);
            const newSize = intersectedBox.getSize(new THREE.Vector3());
            const volume = newSize.x * newSize.y * newSize.z;
            const objectBox = this.getPlanogramObjectFromUid(object.userData.id).groupBoundingBox.clone();
            if (newSize.toArray().every(x => x !== 0) && volume > i.volume)
                return { volume, object, newSize, faceBox, objectBox, n, c, normal };
            return i;
        }, { volume: 0, newSize: new THREE.Vector3(), object: new THREE.Object3D(), faceBox: new THREE.Box3(), objectBox: new THREE.Box3(), n: 0, c: 0, normal: new THREE.Vector3() });

        if (volume === 0) return boxPosition;

        const planObject = this.getPlanogramObjectFromUid(object.userData.id);
        const hasAnchors = planObject?.snapAnchors.length > 0;

        const applyGap = !hasAnchors && newSize.getComponent(c) < (clampDetection - gap) ? gap : 0;
        const transformedVector = boxPosition.clone().setComponent(c, new THREE.Vector3().copy(n > 0 ? faceBox.min : faceBox.max).
            add(new THREE.Vector3().setComponent(c, ((boxSize.getComponent(c) / 2) + applyGap) * n)).getComponent(c));
        const projectedBox = this.dragArrayModel.boxes.reduce((a, c) => a.union(c), new THREE.Box3()).translate(transformedVector);
        // const anchor = planObject?.getNearestAnchor( normal, transformedVector );
        // if(anchor){
        //     if(anchor.forceConstraint)constrainVector = true;
        //     transformedVector.copy(anchor.getFacePosition( normal, transformedVector,boxSize ));
        // }

        this.setSnapDecorator(objectBox, normal);
        return constrainVector ? this.constrainToFace(transformedVector, boxSize, objectBox, normal) : transformedVector;
    }

    #intersectBox = null;
    /**
     * 
     * @param {THREE.Box3} target 
     * @param {THREE.Intersection} intersection 
     * @returns {THREE.Vector3}
     */
    fromIntersection(target, intersection) {
        const constrainToFace = PlanogramRenderer.get(this)?.ctrlKey ?? false;
        var constrainVector = constrainToFace;
        const boxSize = target.getSize(new THREE.Vector3());
        const affectedComponent = intersection.normal.toArray().reduce((a, c, i) => { return (c !== 0 && a === -1) ? i : a; }, -1);
        const unAffectedComponents = [0, 1, 2].filter(x => x !== affectedComponent);
        const normalComponent = intersection.normal.getComponent(affectedComponent);
        const offset = this.#offset.clone();
        var transformedVector = intersection.point.clone();
        const projectedBox = this.dragArrayModel.boxes.reduce((a, c) => a.union(c), new THREE.Box3()).translate(transformedVector);
        const affectFace = (normalComponent < 0 ? projectedBox.max : projectedBox.min).getComponent(affectedComponent);
        const distanceToFace = affectFace - transformedVector.getComponent(affectedComponent);
        transformedVector.setComponent(affectedComponent, transformedVector.getComponent(affectedComponent) - distanceToFace).
            sub(unAffectedComponents.reduce((a, c) => a.setComponent(c, offset.getComponent(c)), new THREE.Vector3())).
            add(new THREE.Vector3(0, affectedComponent != 1 ? offset.y : 0, 0));
        const planObject = this.getPlanogramObjectFromUid(intersection.object.userData.id);
        const anchor = planObject?.getNearestAnchor(intersection.normal, transformedVector);
        if (anchor) {
            if (anchor.forceConstraint) constrainVector = true;
            transformedVector.copy(anchor.getFacePosition(planObject.groupBoundingBox, intersection.normal, transformedVector, boxSize));
        }
        this.setSnapDecorator(new THREE.Box3().setFromObject(intersection.object), intersection.normal);
        return constrainVector ? this.constrainToFace(transformedVector, boxSize, new THREE.Box3().setFromObject(intersection.object), intersection.normal) : transformedVector;
    }

    /**
     * 
     * @param {PlanogramObject} target 
     * @param {THREE.Intersection} intersection 
     */
    fromIntersectionUV(target, intersection) {

    }

    setSnapDecorator(box, normal) {
        if (this.snapDecoration) this.removeSnapDecorator();
        this.snapDecoration = new SnapDecoration(box, normal);
        this.group.add(this.snapDecoration);
    }


    removeSnapDecorator() {
        if (this.snapDecoration) {
            this.snapDecoration.geometry.dispose();
            this.snapDecoration.material.dispose();
            this.snapDecoration.removeFromParent();
            this.snapDecoration = null;
        }
    }
    /**
     * 
     * @param {THREE.Vector3} transformedVector 
     * @param {THREE.Vector3} box1Size 
     * @param {THREE.Box3} box2 
     * @param {THREE.Vector3} normal 
     * @returns {THREE.Vector3}
     */
    constrainToFace(transformedVector, box1Size, box2, normal) {
        const affectedComponent = normal.toArray().reduce((a, c, i) => { return (c !== 0 && a === -1) ? i : a; }, -1);
        const box2Size = box2.getSize(new THREE.Vector3());
        const box2Centre = box2.getCenter(new THREE.Vector3());
        const projectedBox = this.dragArrayModel.boxes.reduce((a, c) => a.union(c), new THREE.Box3()).translate(transformedVector);
        const maxSpace = Array(3).fill(0).reduce((a, c, i) => a.setComponent(i,
            (box1Size.getComponent(i) > box2Size.getComponent(i)) ? ((box1Size.getComponent(i) - box2Size.getComponent(i)) * 2) + box2Size.getComponent(i) : box2Size.getComponent(i)),
            new THREE.Vector3());

        const newBounds = new THREE.Box3().setFromCenterAndSize(box2Centre, maxSpace);
        const constrainedVector = Array(3).fill(0).reduce((a, c, i) => {
            if (i === affectedComponent) a.setComponent(i, 0);
            else if (projectedBox.max.getComponent(i) > newBounds.max.getComponent(i))
                a.setComponent(i, -(projectedBox.max.getComponent(i) - newBounds.max.getComponent(i)));
            else if (projectedBox.min.getComponent(i) < newBounds.min.getComponent(i))
                a.setComponent(i, newBounds.min.getComponent(i) - projectedBox.min.getComponent(i));
            return a;
        }, new THREE.Vector3());

        return transformedVector.clone().add(constrainedVector);
    }


    //#endregion

    //#region Planogram Tab Methods
    #setUpTabItem() {

        if (!this.groupFrameId) {
            this.#tabItem?.remove();
            this.#tabItem = null;
            return;
        }

        if (this.#tabItem) return;

        const topHighlight = "highlight-top";
        const bottomHighlight = "highlight-bottom";
        const leftHighlight = "highlight-left";
        const rightHighlight = "highlight-right";
        const dragThreshold = 5;
        const tabItem = document.createElement("div");
        tabItem.classList.add("tabItem");
        tabItem.id = this.uid;
        tabItem.addEventListener("pointerdown", down => {
            if (this.#closebutton && this.#closebutton.contains(down.target)) return;
            /**@type {HTMLDivElement|undefined} */
            var firstContainer = null;
            /**@type {HTMLDivElement|undefined} */
            var firstTabBar = null;
            /**@type {HTMLDivElement|undefined} */
            var highlightedTabItem = null;

            const rootNode = tabItem.getRootNode();
            const rootRenderer = PlanogramRenderer.get(this);
            down.preventDefault();
            down.stopPropagation();
            tabItem.setPointerCapture(down.pointerId);

            /**@type {HTMLElement} */
            const ghostTab = document.createElement("div");
            ghostTab.classList.add("tabGhost");
            ghostTab.style.left = `${down.clientX}px`;
            ghostTab.style.top = `${down.clientY}px`;
            ghostTab.innerText = tabItem.innerText;
            /**@type {HTMLElement} */

            const tabControlRoot = rootNode.querySelector(".tabControl.root");
            const frames = [...rootRenderer.querySelectorAll('planogram-camera-frame')];
            const cameraFrame = frames.find(f => f.uid === this.groupFrameId);
            // Check if there are other leaf frames (frames with no child frames) that have cameras
            const noOtherCameras = [...rootRenderer.querySelectorAll(`planogram-camera-frame`)].
                filter(x => x.childFrames.length === 0 && x !== cameraFrame).
                flatMap(x => x.associatedCameras).length === 0;
            const siblingLength = !cameraFrame || (cameraFrame?.associatedCameras.length === 1 && noOtherCameras);

            const mousemove = move => {
                if (siblingLength || Math.abs(down.clientX - move.clientX) < dragThreshold && Math.abs(down.clientY - move.clientY) < dragThreshold)
                    return;
                if (!tabControlRoot.contains(ghostTab))
                    tabControlRoot.appendChild(ghostTab);
                rootNode.querySelectorAll(".tabContainer").forEach(x => x.classList.remove(topHighlight, bottomHighlight, leftHighlight, rightHighlight));
                const offset = tabControlRoot.getBoundingClientRect();
                tabItem.style.cursor = "grabbing";
                ghostTab.style.cursor = "grabbing";
                ghostTab.style.left = `${move.clientX - offset.x}px`;
                ghostTab.style.top = `${move.clientY - offset.y}px`;

                [...rootNode.querySelectorAll(".tabBar")].flat().flatMap(x => [...x.children]).forEach(x => x.classList.remove("dragLeft", "dragRight"));

                firstTabBar = [...rootNode.querySelectorAll(".tabBar")].find(x => {
                    const boundingBox = x.getBoundingClientRect();
                    return move.clientX > boundingBox.left && move.clientX < boundingBox.right && move.clientY > boundingBox.top && move.clientY < boundingBox.bottom
                });

                firstContainer = [...rootNode.querySelectorAll(".tabContainer")].
                    filter(x => ![...x.querySelectorAll('*')].some(y => y.classList.contains("tabContainer"))).find(x => {
                        const boundingBox = x.getBoundingClientRect();
                        return move.clientX > boundingBox.left && move.clientX < boundingBox.right && move.clientY > boundingBox.top && move.clientY < boundingBox.bottom
                    });

                // if this tabitem is the only tab item in the tabcontrol then we can't split it's own container
                if (firstContainer) {

                    const frame = this.getCameraFrameFromUID(firstContainer.getAttribute(PlanogramCameraFrame.UnobservedAttributes.AttachedFrame));
                    if (frame && frame.canSplit(this.#tabItem)) {
                        const isSibling = frame.isSiblingFrame(this.#tabItem);
                        const parentDirection = frame.parentElement.childFrameDirection;
                        const rect = firstContainer.getBoundingClientRect();
                        const verticalMid = rect.top + rect.height / 2;
                        const horizontalMid = rect.left + rect.width / 2;
                        const dx = move.clientX - horizontalMid;
                        const dy = move.clientY - verticalMid;
                        firstContainer.classList.remove(topHighlight, bottomHighlight, leftHighlight, rightHighlight);
                        if (isSibling) {
                            if (parentDirection === PlanogramCameraFrame.frameType.Horizontal)
                                firstContainer.classList.add(move.clientY > verticalMid ? bottomHighlight : topHighlight);
                            else if (parentDirection === PlanogramCameraFrame.frameType.Vertical)
                                firstContainer.classList.add(move.clientX > horizontalMid ? rightHighlight : leftHighlight);
                        } else {
                            if (Math.abs(dx) > Math.abs(dy)) {
                                if (!isSibling || isSibling && parentDirection === PlanogramCameraFrame.frameType.Vertical)
                                    firstContainer.classList.add(dx > 0 ? rightHighlight : leftHighlight);
                            } else {
                                if (!isSibling || isSibling && parentDirection === PlanogramCameraFrame.frameType.Horizontal)
                                    firstContainer.classList.add(dy > 0 ? bottomHighlight : topHighlight);
                            }
                        }

                    }
                } else if (firstTabBar) {
                    const childTabItems = [...firstTabBar.children].filter(x => x.classList.contains("tabItem") && x !== tabItem);
                    highlightedTabItem = childTabItems.find((x, i) => {
                        const itemRect = x.getBoundingClientRect();
                        const isRight = move.clientX >= itemRect.x + itemRect.width / 2 && (move.clientX < itemRect.right || childTabItems.length === i + 1);
                        const isLeft = move.clientX < itemRect.x + itemRect.width / 2 && move.clientX > itemRect.left;
                        const value = isRight && x.nextElementSibling !== tabItem ? 1 : isLeft && x.previousElementSibling !== tabItem ? -1 : 0;
                        if (value !== 0) x.classList.add(value === 1 ? "dragRight" : value === -1 ? "dragLeft" : "");
                        return value !== 0;
                    });
                }

            };
            const mouseup = up => {
                tabItem.removeEventListener("pointermove", mousemove);
                tabItem.removeEventListener("pointerup", mouseup);
                ghostTab.remove();
                up.stopPropagation();

                if (Math.abs(down.clientX - up.clientX) < dragThreshold && Math.abs(down.clientY - up.clientY) < dragThreshold) {
                    if (!this.active)
                        this.raiseClickEvent();
                    else if (this.active && !this.focused)
                        this.raiseFocusEvent();
                } else {
                    if (firstContainer) {
                        const frame = this.getCameraFrameFromUID(firstContainer.getAttribute(PlanogramCameraFrame.UnobservedAttributes.AttachedFrame));
                        const anchor = firstContainer.classList.contains(topHighlight) ? 0 :
                            firstContainer.classList.contains(bottomHighlight) ? 1 :
                                firstContainer.classList.contains(leftHighlight) ? 2 :
                                    firstContainer.classList.contains(rightHighlight) ? 3 : null;
                        frame.raiseDropEvent(anchor, this.id);

                    } else if (highlightedTabItem) {
                        const anchor = highlightedTabItem.classList.contains("dragLeft") ? 2 :
                            highlightedTabItem.classList.contains("dragRight") ? 3 : null;
                        const camera = this.getCameraFromUID(highlightedTabItem.id);
                        this.raiseDropEvent(anchor, camera.id);
                    }

                }

                [...rootNode.querySelectorAll(".tabBar")].flatMap(x => [...x.children]).forEach(x => x.classList.remove("dragLeft", "dragRight"));
                [...rootNode.querySelectorAll(".tabContainer")].forEach(x => x.classList.remove(topHighlight, bottomHighlight, leftHighlight, rightHighlight));
                tabItem.releasePointerCapture(down.pointerId);
                tabItem.style.cursor = "pointer";

            };
            tabItem.addEventListener("pointermove", mousemove);
            tabItem.addEventListener("pointerup", mouseup);
        });

        tabItem.addEventListener("contextmenu", e => {
            e.preventDefault();
            e.stopPropagation();
            this.raiseContextMenuEvent(e.clientX, e.clientY);
        });
        this.#tabItem = tabItem;
        this.setActiveAndFocused();
        this.#setTabItemText();
        this.setTabDraggable();
        this.#setUpCloseButton()
    }

    #setTabItemText() {
        if (!this.#text) {
            this.#text = document.createElement("span");
            this.#tabItem.appendChild(this.#text);
        }
        const planName = this.groupName ?? this.planogramName;
        this.#text.innerText = planName;
    }

    #setUpCloseButton() {
        if (!this.disableClose && !this.#closebutton) {
            const button = document.createElement("button");
            button.innerHTML = `<svg height="16px" width="16px" xmlns="http://www.w3.org/2000/svg">
                <line x1="4" y1="4" x2="12" y2="12"></line>
                <line x1="12" y1="4" x2="4" y2="12"></line>
            </svg>`;
            button.addEventListener("click", e => {
                e.preventDefault();
                e.stopPropagation();

                this.raiseCancelEvent();
            });
            this.#closebutton = button;
            this.#tabItem.appendChild(button);
        }
        if (!this.disableClose && !this.#tabItem.contains(this.#closebutton)) this.#tabItem.appendChild(this.#closebutton);
        else if (this.disableClose && this.#tabItem.contains(this.#closebutton)) this.#closebutton.remove();

    }

    setTabDraggable() {
        this.#tabItem.draggable = true;
    }

    setActiveAndFocused() {
        if (this.isConnected && this.#tabItem && this.#tabItem.parentElement)
            this.cameraFrame?.updateFocus();
        this.activeCameras.at(0)?.setActiveAndFocused();
    }
    toolBarAssigned() {

    }
    //#endregion

    //#region Drag interaction Methods

    // /**
    //  * 
    //  * @param {Number} x
    //  * @param {Number} y 
    //  * @returns {{id:string, position:{x:number,y:number,z:number}, container:PlanogramContainer|null, target:PlanogramObject|null}}
    //  */
    // getTargetUnderCursor( x, y ){

    //     const container = this.focusedCamera?.container;
    //     const intersections = this.#getRayIntersections( x, y );
    //     if(intersections === null)return null;

    //     /**
    //      * @type {{id:string, position:{x:number,y:number,z:number}, container:PlanogramContainer, target:PlanogramObject|null}[]}
    //      */
    //     var [ firstItem ] = intersections.map( intersect => {
    //         var current = intersect.object;
    //         let {x ,y, z} = intersect.point;
    //         if(Reflect.has(current.userData, "id")){
    //             const target = [...container.querySelectorAll("*")].find(x=>x.uid === current.userData.id);
    //             return { ...current.userData, ...{ position: {x, y, z} }, container:container, target:target };
    //         }
    //         return { ...current.userData, ...{ position: {x, y, z} }, container:container, target:null };
    //     }).
    //     filter(x=>{
    //         return x.target instanceof PlanogramPosition && !this.ignorePositions || x.target instanceof PlanogramSubcomponent && !this.ignoreSubcomponents;
    //     }).reduce((a,c) => !a.some( x => x.id == c.id ) ? a.concat([c]) : a, []);

    //     return firstItem;
    // }

    /**
     * Returns the translated cursor position based on the dock vector of the focused camera
     * @param {number} x 
     * @param {number} y 
     * @param {boolean} drag 
     * @returns {THREE.Vector2}
     */
    #getTranslatedCursor(x, y, drag = false) {

        if (!this.focusedCamera?.dockVector) return new THREE.Vector2(x, y);
        const dockVector = this.focusedCamera.dockVector.clone();

        if (drag) {
            dockVector.x = 0;
            dockVector.y = 0;
        }
        var translatedCursor = new THREE.Vector2(
            ((x - dockVector.x) / dockVector.z) * 2 - 1,
            -((y - dockVector.y) / dockVector.w) * 2 + 1
        );

        return translatedCursor;
    }
    /**
     * 
     * @param {number} x 
     * @param {number} y 
     * @returns {THREE.Intersection[]|[]}
     */
    #getRayIntersections(x, y) {
        if (!this.focusedCamera) return null;
        const translatedCursor = this.#getTranslatedCursor(x, y);
        if (!translatedCursor) return null;
        var raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(translatedCursor, this.focusedCamera.camera);
        return raycaster.intersectObjects(this.group.children) ?? [];
    }

    /**
    * 
    * @param {number} x 
    * @param {number} y 
    * @param {PlanogramObject[]} ignore 
    * @returns {THREE.Intersection|null}
    */
    #getFirstIntersection(x, y, ignore, ignoreNames = []) {
        const renderer = PlanogramRenderer.get(this);

        const interactions = [];
        if (renderer.ignorePositions === false && !ignoreNames.includes(PlanogramPosition.name)) interactions.push(PlanogramPosition.name);
        if (renderer.ignoreSubcomponents === false && !ignoreNames.includes(PlanogramComponent.name)) interactions.push(PlanogramComponent.name);
        if (renderer.includeBlocking && !ignoreNames.includes(PlanogramBlocking.name)) interactions.push(PlanogramBlocking.name);
        if (interactions.length === 0) return null;

        const ignoreIds = ignore?.map(x => x.uid) ?? [];
        const intersections = this.#getRayIntersections(x, y);
        const [first] = intersections.filter(x => interactions.includes(x.object.name) && !ignoreIds.includes(x.object.userData.id));
        return first ?? null;
    }

    /**
     * 
     * @param {THREE.Vector2} cursorPosition 
     */
    setDragArrayPosition(cursorPosition) {
        if (this.dragArrayModel) {
            this.clearDecorators();
            this.dragArrayModel.anchorData = null;
            const intersection = this.#getFirstIntersection(cursorPosition.x, cursorPosition.y, this.dragArrayModel?.planogramObjects, this.getIgnoreList(this.dragArrayModel?.target?.constructor.name));
            const box = new THREE.Box3().setFromCenterAndSize(this.#dragVector, this.dragArrayModel.target.groupBoundingBox.getSize(new THREE.Vector3()));
            if (intersection && Reflect.has(intersection.object.userData, "id")) {
                const intersectedObject = this.getPlanogramObjectFromUid(intersection.object.userData.id);
                if (intersectedObject) {
                    if (this.dragArrayModel.target instanceof PlanogramPosition) {
                        const rayCaster = new THREE.Raycaster();
                        rayCaster.setFromCamera(this.#getTranslatedCursor(cursorPosition.x, cursorPosition.y), this.focusedCamera.camera);
                        const worldBoundingBox = intersectedObject.groupBoundingBox.clone().applyMatrix4(intersectedObject.group.matrixWorld);
                        const intersectionPoint = rayCaster.ray.intersectBox(worldBoundingBox, this.#intersectTarget);
                        const faceNormal = intersectedObject.setDecoratorFromPoint(intersectionPoint);
                        this.dragArrayModel.anchorData = new AnchorData(faceNormal, intersectedObject);
                    } else {
                        const transformedVector = this.fromIntersection(box, intersection);
                        this.#dragVector.copy(transformedVector);
                    }
                }
                // }else if( renderer.snappingDistance > 0 && renderer.shiftKey ){
                //     const transformedVector = this.#getNearestObject( this.#dragVector );
                //     if(this.#dragVector.equals(transformedVector)) this.removeSnapDecorator();
                //     this.#dragVector.copy(transformedVector);
            } else {
                this.removeSnapDecorator();
            }
            this.dragArrayModel.position.copy(this.#dragVector);
            this.dragArrayModel.renderOrder = 9999;
        }
    }

    getIgnoreList(targetName) {
        const ignoreNames = [];
        if (targetName === PlanogramComponent.name) ignoreNames.push(PlanogramPosition.name);
        return ignoreNames;
    }

    setDividerPosition() {
        // Prefer pointer drag target when present (touch/pointer), otherwise use the mouse drag target
        const divider = this.#pointerDragTarget ?? this.#targetDraggable;
        if (!(divider instanceof PlanogramBlockingDivider)) return;
        const planBox = this.planogramBox.clone();
        const planSize = planBox.getSize(new THREE.Vector3());
        const planMin = planBox.min.clone();
        const length = divider.length;
        const dir = divider.direction;

        const pointerPos = this.#dragVector.clone();
        const offset = this.#dividerDragOffset ?? new THREE.Vector3();
        const dragCenter = pointerPos.clone().sub(offset);
        const existingNormX = divider.x;
        const existingNormY = divider.y;

        let originalCenterX, originalCenterY;
        if (dir === 'horizontal') {
            originalCenterX = planMin.x + (existingNormX * planSize.x) + (length * planSize.x / 2);
            originalCenterY = planMin.y + (existingNormY * planSize.y);
        } else {
            originalCenterX = planMin.x + (existingNormX * planSize.x);
            originalCenterY = planMin.y + (existingNormY * planSize.y) + (length * planSize.y / 2);
        }

        if (dir === 'horizontal') {
            dragCenter.x = originalCenterX;
        } else {
            dragCenter.y = originalCenterY;
        }

        let normX, normY;
        if (dir === 'horizontal') {
            normX = existingNormX;
            normY = (dragCenter.y - planMin.y) / planSize.y;
            normY = THREE.MathUtils.clamp(normY, 0, 1);
        } else {
            normX = (dragCenter.x - planMin.x) / planSize.x;
            normX = THREE.MathUtils.clamp(normX, 0, 1);
            normY = existingNormY;
            normY = THREE.MathUtils.clamp(normY, 0, 1 - length);
        }
        if (this.#dividerOutline && this.#dividerOutlineTarget === divider) {
            this.#dividerOutline.userData.normX = normX;
            this.#dividerOutline.userData.normY = normY;
            const center = new THREE.Vector3();
            if (dir === 'horizontal') {
                center.set(planMin.x + (normX * planSize.x) + (length * planSize.x / 2), planMin.y + (normY * planSize.y), planSize.z / 2);
            } else {
                center.set(planMin.x + (normX * planSize.x), planMin.y + (normY * planSize.y) + (length * planSize.y / 2), planSize.z / 2);
            }
            this.#dividerOutline.position.copy(center);
        }
    }

    /**
     * 
     * @param {PlanogramBlockingDivider} divider 
     */
    #createDividerOutline(divider) {
        try {
            const planBox = this.planogramBox.clone();
            const planSize = planBox.getSize(new THREE.Vector3());
            const dims = divider.getDimensions(planSize, true);
            const center = divider.getPosition(planSize);

            const geom = new THREE.BoxGeometry(dims.x, dims.y, planSize.z);
            const mat = new THREE.MeshBasicMaterial({ color: 0x999999, transparent: true, opacity: 0.9 });
            const outline = new THREE.Mesh(geom, mat);
            outline.position.copy(center);
            outline.renderOrder = 9999;
            outline.userData = { dividerUid: divider.uid, normX: divider.x, normY: divider.y };
            outline.layers.set(PlanogramRendererResources.DECORATOR_LAYER);
            this.group.add(outline);
            this.#dividerOutline = outline;
            this.#dividerOutlineTarget = divider;
        } catch (e) { console.warn('Failed to create divider outline', e); }
    }

    #removeDividerOutline(commit = false) {
        if (!this.#dividerOutline) return;
        if (commit && this.#dividerOutlineTarget) {
            /**@type {PlanogramBlockingDivider} */
            const divider = this.#dividerOutlineTarget;
            const outline = this.#dividerOutline;
            const planBox = this.planogramBox.clone();
            const planSize = planBox.getSize(new THREE.Vector3());
            const planMin = planBox.min.clone();
            const length = divider.length || 0;
            const pos = outline.position;
            let normX = 0, normY = 0;
            if (divider.direction === 'horizontal') {
                normX = (pos.x - planMin.x - (length * planSize.x / 2)) / planSize.x;
                normY = (pos.y - planMin.y) / planSize.y;
                normX = THREE.MathUtils.clamp(normX, 0, 1);
                normY = THREE.MathUtils.clamp(normY, 0, 1);
            } else {
                normX = (pos.x - planMin.x) / planSize.x;
                normY = (pos.y - planMin.y - (length * planSize.y / 2)) / planSize.y;
                normX = THREE.MathUtils.clamp(normX, 0, 1);
                normY = THREE.MathUtils.clamp(normY, 0, Math.max(0, 1 - length));
            }

            divider.raiseMoveEvent(Math.round(THREE.MathUtils.clamp(normX, 0, 1) * 1000), Math.round(THREE.MathUtils.clamp(normY, 0, 1) * 1000));

        }
        // dispose outline geometry/material and remove from scene
        try { this.#dividerOutline.geometry.dispose(); } catch (e) { }
        try { this.#dividerOutline.material.dispose(); } catch (e) { }
        this.#dividerOutline.removeFromParent();
        this.#dividerOutline = null;
        this.#dividerOutlineTarget = null;
    }

    clearDecorators() {
        this.scene.getObjectsByProperty("name", "Decorator").
            filter(x => x instanceof THREE.InstancedMesh).map(x => x.geometry).
            filter(x => x instanceof THREE.BoxGeometry).forEach(x => {
                if (!x.attributes.direction.array.slice(0, 2).every(x => x === 1)) {
                    x.attributes.direction.array.fill(0);
                    x.attributes.direction.needsUpdate = true;
                }
            });
    }

    #updateDragVectorFromCursor(x, y) {
        const translatedCursor = this.#getTranslatedCursor(x, y, true);
        this.#raycaster.setFromCamera(translatedCursor, this.focusedCamera.camera);

        if (!this.#raycaster.ray.intersectPlane(this.#plane, this.#intersection)) {
            return false;
        }

        this.#dragVector.copy(this.#intersection)
            .sub(this.#offset)
            .applyMatrix4(this.#inverseMatrix);
        return true;
    }

    #panCameraForDraggedTarget(targetPosition) {
        const camera = this.focusedCamera;
        const dragTarget = targetPosition?.clone();
        const dock = camera?.dockVector;

        if (!camera || !dragTarget || !dock || dock.z <= 0 || dock.w <= 0) {
            return false;
        }

        const projected = dragTarget.project(camera.camera);
        if (![projected.x, projected.y, projected.z].every(Number.isFinite)) {
            return false;
        }

        const viewportLeft = dock.x;
        const viewportTop = dock.y;
        const viewportRight = dock.x + dock.z;
        const viewportBottom = dock.y + dock.w;
        const thresholdX = Math.min(Math.max(dock.z * 0.15, 32), 96);
        const thresholdY = Math.min(Math.max(dock.w * 0.15, 32), 96);
        const maxPanPixels = 32;
        const screenX = viewportLeft + ((projected.x + 1) * 0.5 * dock.z);
        const screenY = viewportTop + ((1 - projected.y) * 0.5 * dock.w);

        let panX = 0;
        let panY = 0;

        if (screenX < viewportLeft + thresholdX) {
            panX = screenX - (viewportLeft + thresholdX);
        } else if (screenX > viewportRight - thresholdX) {
            panX = screenX - (viewportRight - thresholdX);
        }

        if (screenY < viewportTop + thresholdY) {
            panY = screenY - (viewportTop + thresholdY);
        } else if (screenY > viewportBottom - thresholdY) {
            panY = screenY - (viewportBottom - thresholdY);
        }

        if (Math.abs(panX) < 0.001 && Math.abs(panY) < 0.001) {
            return false;
        }

        return camera.panByViewportPixels(
            THREE.MathUtils.clamp(panX, -maxPanPixels, maxPanPixels),
            THREE.MathUtils.clamp(panY, -maxPanPixels, maxPanPixels)
        );
    }

    /**
     * Calculates 
     * @param {number} x 
     * @param {number} y 
     * @param {boolean} initial 
     */
    setDrag(x, y, initial = false) {
        if (initial) {
            const first = this.overObject(x, y);
            if (first && (this.#targetOnDown === null || first.object instanceof PlanogramObject && this.#targetDraggable instanceof PlanogramObject && first.object.uid !== this.#targetDraggable.uid)) return;
        }
        if (this.isDraggable) {
            const translatedCursor = this.#getTranslatedCursor(x, y, true);
            this.#raycaster.setFromCamera(translatedCursor, this.focusedCamera.camera);
            const group = this.#targetDraggable.group;
            if (initial) this.#plane.setFromNormalAndCoplanarPoint(this.focusedCamera.camera.getWorldDirection(this.#plane.normal),
                this.#worldPosition.setFromMatrixPosition(group.matrixWorld));
            if (this.#raycaster.ray.intersectPlane(this.#plane, this.#intersection)) {
                if (initial) {
                    this.#inverseMatrix.copy(this.group.matrixWorld).invert();
                    this.#offset.copy(this.#intersection).sub(this.#worldPosition.setFromMatrixPosition(group.matrixWorld));
                    if (this.#targetDraggable instanceof PlanogramBlockingDivider) {
                        const planBox = this.planogramBox.clone();
                        const planSize = planBox.getSize(new THREE.Vector3());
                        const planMin = planBox.min.clone();
                        const divider = this.#targetDraggable;
                        const dividerCenterLocal = planMin.clone().add(divider.getPosition(planSize));
                        const pointerLocal = this.#intersection.clone().sub(this.#offset).applyMatrix4(this.#inverseMatrix);
                        this.#dividerDragOffset = pointerLocal.clone().sub(dividerCenterLocal);
                        this.#createDividerOutline(divider);
                    } else {
                        this.#dividerDragOffset = null;
                    }
                } else {
                    if (!this.#updateDragVectorFromCursor(x, y)) {
                        return;
                    }

                    if (this.#targetDraggable instanceof PlanogramBlockingDivider) {
                        this.setDividerPosition(new THREE.Vector2(x, y));
                    } else {
                        const cursorPosition = new THREE.Vector2(x, y);
                        this.setDragArrayPosition(cursorPosition);

                        if (this.#panCameraForDraggedTarget(this.dragArrayModel?.position) && this.#updateDragVectorFromCursor(x, y)) {
                            this.setDragArrayPosition(cursorPosition);
                        }
                    }
                }
            }
        }
    }

    onMouseDown(x, y, leftClick = true) {
        this.#downPosition = new THREE.Vector2(x, y);
        const result = this.overObject(x, y, true);
        this.#targetOnDown = result?.object;
        if (leftClick && this.#targetOnDown) {
            const planogramBlocking = this.querySelector("planogram-blocking");
            const isAddDivider = planogramBlocking && (planogramBlocking.addVerticalDivider || planogramBlocking.addHorizontalDivider);
            const isBlocking = result.intersection.object.name === PlanogramBlocking.name && isAddDivider;
            if (this.#targetOnDown instanceof PlanogramBlockingDivider && !isBlocking) {
            } else if (isBlocking) {
                planogramBlocking.raiseClickEvent(result.intersection);
            }
        }
    }

    onMouseUp(x, y, raiseClick = true) {
        const intersection = this.#getFirstIntersection(x, y);
        const withinThreshold = PlanogramContainer.isWithinThreshold(new THREE.Vector2(x, y), this.#downPosition);
        if (intersection) {
            const planogramBlocking = this.querySelector("planogram-blocking");
            const isAddDivider = planogramBlocking && (planogramBlocking.addVerticalDivider || planogramBlocking.addHorizontalDivider);
            const isBlocking = intersection.object.name === PlanogramBlocking.name && isAddDivider;
            if (withinThreshold) {
                if (raiseClick) {
                    if (this.#targetOnDown && intersection) {
                        if (this.#targetOnDown.uid === intersection.object.userData.id) {
                            this.#targetOnDown.raiseClickEvent(x, y, true);
                        } else {
                            const blocking = planogramBlocking.getIntersectedBox(intersection);
                            if (blocking instanceof PlanogramBlockingPosition) {
                                blocking.raiseClickEvent(x, y, true);
                            } else if (blocking instanceof PlanogramBlockingDivider) {
                                blocking.raiseClickEvent(x, y, true);
                            }
                        }
                    }
                } else {
                    if (!isBlocking) {
                        if (this.#targetOnDown instanceof PlanogramBlockingDivider) {
                            this.#targetOnDown.raiseClickEvent(x, y, false);
                        } else {
                            this.#targetOnDown.raiseClickEvent(x, y, raiseClick);
                        }
                    }
                }
            }
        } else if (withinThreshold) {
            this.raiseClickEvent(x, y, raiseClick);
        }
        if (this.#dividerOutline) {
            this.#removeDividerOutline(true);
        }
        this.#targetOnDown = null;
        this.#downPosition = null;
    }

    /**
     * @param {number} x
     * @param {number} y
     * @returns {{ cursor: string, isDraggable:boolean, object: PlanogramObject|PlanogramBlockingDivider|PlanogramBlockingPosition|PlanogramSubcomponent, intersection: THREE.Intersection }|null}
     */
    overObject(x, y, raiseClick = false) {
        const blocking = this.querySelector("planogram-blocking");
        const intersection = this.#getFirstIntersection(x, y);
        if (!intersection) {
            blocking?.updateShaderAttributes(-1);
            return null;
        }
        const planObject = this.getPlanogramObjectFromUid(intersection.object.userData.id);
        if (!planObject) return null;
        const options = { cursor: 'default', isDraggable: true, object: planObject, intersection: intersection };
        if (intersection.object.name === PlanogramBlocking.name || intersection.object.name === PlanogramComponent.name) {
            if (planObject instanceof PlanogramBlocking) {
                const item = planObject.getIntersectedBox(intersection) ?? planObject;
                if (item instanceof PlanogramBlockingDivider && (!blocking.addVerticalDivider && !blocking.addHorizontalDivider)) {
                    blocking?.updateShaderAttributes(-1);
                    return { ...options, isDraggable: false, cursor: "grab", object: item };
                } else if (item instanceof PlanogramBlockingPosition) {
                    planObject.updateShaderAttributes(intersection.instanceId, intersection.uv, intersection.normal);
                    return { ...options, isDraggable: false, cursor: "default", object: item };
                } else {
                    if (blocking.addVerticalDivider || blocking.addHorizontalDivider) {
                        blocking.updateShaderAttributes(intersection.instanceId, intersection.uv, intersection.normal);
                    } else {
                        blocking.updateShaderAttributes(-1);
                    }
                }
            }
            if (planObject instanceof PlanogramComponent && planObject.editCustom) {
                const subcomponent = planObject.getTargetedSubComponent(intersection);
                return (subcomponent?.selected ?? false) ? { ...options, object: subcomponent } : null;
            }
        }
        return (raiseClick || planObject.selected) ? options : null;
    }

    //#endregion

    //#region DragArray Methods

    /**
     * Constructs a drag array from the selected objects
     * @param {DataTransfer} dataTransfer 
     */
    constructDragArray(dataTransfer) {
        if (this.isDraggable && !(this.#targetOnDown instanceof PlanogramBlockingDivider)) {
            const items = [...this.querySelectorAll("[selected]")];
            this.dragArrayModel = new DragArray(this.#targetDraggable, items, dataTransfer);
            this.focusedCamera.camera.layers.enable(PlanogramRendererResources.DRAG_LAYER);
            this.group.add(this.dragArrayModel);
        }
    }

    /**
     * Drops the drag array and can raise the drop event
     * @param {boolean} raiseEvent 
     */
    clearDragArray(raiseEvent = false) {
        if (this.dragArrayModel) {
            this.clearDecorators();
            this.dragArrayModel.dropDragArray(raiseEvent);
            this.dragArrayModel = null;
            this.focusedCamera.camera.layers.disable(PlanogramRendererResources.DRAG_LAYER);
        }
        this.removeSnapDecorator();
        // remove any divider outline preview if present
        this.#removeDividerOutline(raiseEvent);
    }


    //#endregion

    //#region Pointer Event Handlers for Divider Dragging

    /**
     * Handle pointer down - start divider drag if applicable
     * @param {number} x 
     * @param {number} y 
     * @param {number} pointerId 
     */
    onPointerDown(x, y, pointerId) {
        // Check if we're over a draggable divider
        const result = this.overObject(x, y, false);
        const blocking = this.querySelector("planogram-blocking");
        if (blocking && !blocking.disabled && result && result.object instanceof PlanogramBlockingDivider) {
            // Start pointer-based divider drag
            this.#pointerDragging = true;
            this.#activePointerId = pointerId;
            this.#pointerDragTarget = result.object;

            // Initialize drag plane and offset (same as existing drag logic)
            const translatedCursor = this.#getTranslatedCursor(x, y, true);
            this.#raycaster.setFromCamera(translatedCursor, this.focusedCamera.camera);
            const group = this.#pointerDragTarget.group;

            this.#plane.setFromNormalAndCoplanarPoint(
                this.focusedCamera.camera.getWorldDirection(this.#plane.normal),
                this.#worldPosition.setFromMatrixPosition(group.matrixWorld)
            );

            if (this.#raycaster.ray.intersectPlane(this.#plane, this.#intersection)) {
                this.#inverseMatrix.copy(this.group.matrixWorld).invert();
                this.#offset.copy(this.#intersection).sub(this.#worldPosition.setFromMatrixPosition(group.matrixWorld));

                // Calculate divider-specific drag offset
                const planBox = this.planogramBox.clone();
                const planSize = planBox.getSize(new THREE.Vector3());
                const planMin = planBox.min.clone();
                const divider = this.#pointerDragTarget;
                const dividerCenterLocal = planMin.clone().add(divider.getPosition(planSize));
                const pointerLocal = this.#intersection.clone().sub(this.#offset).applyMatrix4(this.#inverseMatrix);
                this.#dividerDragOffset = pointerLocal.clone().sub(dividerCenterLocal);

                // Create visual preview
                this.#createDividerOutline(divider);
            }

            // Prevent default to avoid triggering drag-and-drop
            return true;
        }

        return false;
    }

    /**
     * Handle pointer move - update divider position if dragging
     * @param {number} x 
     * @param {number} y 
     * @param {number} pointerId 
     */
    onPointerMove(x, y, pointerId) {
        if (!this.#pointerDragging || pointerId !== this.#activePointerId || !this.#pointerDragTarget) {
            return;
        }

        // Update drag position using raycasting
        const translatedCursor = this.#getTranslatedCursor(x, y, true);
        this.#raycaster.setFromCamera(translatedCursor, this.focusedCamera.camera);

        if (this.#raycaster.ray.intersectPlane(this.#plane, this.#intersection)) {
            this.#dragVector = this.#intersection
                .sub(this.#offset)
                .applyMatrix4(this.#inverseMatrix);

            this.setDividerPosition();
        }
    }

    /**
     * Handle pointer up - commit divider drag
     * @param {number} x 
     * @param {number} y 
     * @param {number} pointerId 
     */
    onPointerUp(x, y, pointerId) {
        if (!this.#pointerDragging || pointerId !== this.#activePointerId) {
            return;
        }

        // Commit the divider position change
        this.#removeDividerOutline(true);

        // Reset pointer drag state
        this.#pointerDragging = false;
        this.#activePointerId = null;
        this.#pointerDragTarget = null;
        this.#dividerDragOffset = null;
    }

    /**
     * Handle pointer cancel - abort divider drag
     * @param {number} pointerId 
     */
    onPointerCancel(pointerId) {
        if (!this.#pointerDragging || pointerId !== this.#activePointerId) {
            return;
        }

        // Cancel without committing
        this.#removeDividerOutline(false);

        // Reset pointer drag state
        this.#pointerDragging = false;
        this.#activePointerId = null;
        this.#pointerDragTarget = null;
        this.#dividerDragOffset = null;
    }

    //#endregion

    //#region Static Methods
    /**
     * 
     * @param {PlanogramObject|PlanogramProduct} element 
     * @returns {PlanogramContainer|null}
     */
    static get(element) {
        if (!element) return;
        var parent = element.parentElement;
        while (parent !== null && !(parent instanceof PlanogramContainer)) {
            parent = parent.parentElement;
        }
        return parent;
    }

    /**
   * 
   * @param {THREE.Vector2} up 
   * @param {THREE.Vector2} down 
   * @param {THREE.Vector2} threshold  
   * @returns {boolean}
   */
    static isWithinThreshold(up, down, threshold = new THREE.Vector2(3, 3)) {
        if (!up || !down) return false;
        return up.x >= (down.x - threshold.x) && up.x <= (down.x + threshold.x) &&
            up.y >= (down.y - threshold.y) && up.y <= (down.y + threshold.y);
    }

    //#endregion

    //#region Event Raise Methods

    raiseClickEvent(x, y, rightClick = false) {
        const renderer = PlanogramRenderer.get(this);
        const rect = renderer.domElement.getBoundingClientRect();
        const clickEvent = new MouseEvent("click", { bubbles: true, cancelable: true, view: window, clientX: rect.x + x, clientY: rect.y + y });
        Object.defineProperty(clickEvent, 'button', { ...Object.getOwnPropertyDescriptor(clickEvent, 'button'), get: (function () { return rightClick ? 2 : 0; }).bind(this) });
        Object.defineProperty(clickEvent, 'target', { ...Object.getOwnPropertyDescriptor(clickEvent, 'target'), get: (function () { return this; }).bind(this) });
        if (this.dispatchEvent(clickEvent) && renderer.showMessages) {
            console.groupCollapsed("Empty Selection" + (rightClick ? " and Context Menu" : ""));
            console.log("Target: ", this);
            console.log("Event: ", clickEvent);
            console.groupEnd();
        }
    }

    raiseContextMenuEvent() {

    }

    dispatched(event) {
        const renderer = PlanogramRenderer.get(this)?.showMessages ?? false;
        const dropped = this.dispatchEvent(event);
        return dropped && renderer;
    }

    //#endregion

    //#region WebComponent Lifecycle CallBacks
    connectedCallback() {
        this.#setUpScene();
        this.#firstRender = false;
        const frames = [...PlanogramRenderer.get(this).querySelectorAll('planogram-camera-frame')];
        const associatedFrame = frames.find(f => f.uid === this.groupFrameId);
        if (associatedFrame && associatedFrame instanceof PlanogramCameraFrame)
            associatedFrame.addCameras();
        [...this.querySelectorAll('planogram-blocking')].forEach(x => x.applyShowPositionsThroughBlocking?.(this.showPositionsThroughBlocking));
        [...this.querySelectorAll('planogram-position')].forEach(x => { if (x.transparencyChanged) x.transparencyChanged(); });
        [...this.querySelectorAll('planogram-text')].forEach(x => x.applyShowPositionsThroughBlocking?.(this.showPositionsThroughBlocking));
        [...this.querySelectorAll('planogram-label')].forEach(x => x.applyShowPositionsThroughBlocking?.(this.showPositionsThroughBlocking));

        const toolBar = document.createElement("slot");
        toolBar.name = "toolbar";
        toolBar.slot = "toolbar-frame1";
        this.shadowRoot.appendChild(toolBar);

        // Notify all associated cameras that the container is ready
        this.#notifyCamerasReady();
    }

    /**
     * Notify all cameras associated with this container that it's ready.
     * Handles the case where cameras load before the container.
     */
    #notifyCamerasReady() {
        if (!this.containerId) return;

        const renderer = PlanogramRenderer.get(this);
        if (!renderer) return;

        const cameras = [...renderer.querySelectorAll(`planogram-camera[container-id='${this.containerId}']`)];
        cameras.forEach(camera => {
            // Use queueMicrotask to avoid blocking the main thread
            queueMicrotask(() => {
                if (camera.onContainerReady) {
                    camera.onContainerReady();
                }
            });
        });
    }

    disconnectedCallback() {
        this.#scene.removeFromParent();
        this.#scene = null;
        this.#removeDividerOutline(false);
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (this.#firstRender || oldValue === newValue) return;
        if (name === PlanogramContainer.Attributes.HideChestWalls) {
            [...this.querySelectorAll("planogram-component")].forEach(x => x.subComponentPlug(true));
        } else if (name === PlanogramContainer.Attributes.ShowPositionsThroughBlocking) {
            [...this.querySelectorAll('planogram-blocking')].forEach(x => x.applyShowPositionsThroughBlocking?.(this.showPositionsThroughBlocking));
            [...this.querySelectorAll('planogram-position')].forEach(x => { if (x.transparencyChanged) x.transparencyChanged(); });
            [...this.querySelectorAll('planogram-text')].forEach(x => x.applyShowPositionsThroughBlocking?.(this.showPositionsThroughBlocking));
            [...this.querySelectorAll('planogram-label')].forEach(x => x.applyShowPositionsThroughBlocking?.(this.showPositionsThroughBlocking));
        }
    }
    //#endregion


}